import React, { useCallback, useEffect, useState } from "react";
import { Button } from "../../../../../../components/common/Button";
import { ReactComponent as NotifyIcon } from "../../../../assets/notify-icon.svg";
import {
  getCNYQuotationCategory,
  KlashaCNYQuotation,
} from "../../../../redux/slice";
import {
  BalanceExceptionWrapper,
  ContentWrapper,
  Error,
  GenericSubTitle,
  GenericTitle,
  InputWrapper,
  ItemWrapper,
  RWrapper,
} from "../styles";
import CustomSelect from "../../../../../../components/common/CustomSelect";
import Loader from "../../../../../../components/common/Loader";
import InputAndSelect from "../../../../../../components/common/InputAndSelect";
import { RateBox } from "../../../../components/Withdrawal/styles";
import { useAppDispatch, useAppSelector } from "../../../../../../app/hooks";
import { Link, useParams } from "react-router-dom";
import {
  allowedPurposeMap,
  cnyPaymentPurpose,
  cnyTypeOfpaymentMethod,
  getFilteredPaymentMethods,
  paymentMethodConfigurations,
  receiverTypeMap,
  senderTypeMap,
} from "../../../../const";
import { debounce } from "lodash";
import { savedCNYData } from "../../../../redux/formDataSlice";
import { encryptAlgorithm } from "../../../../../../utils/encryptionAlgorithm";
import { merchantBusinessKeys } from "../../../../../Settings/settingsSlice";
import { formatCurrencyAmount } from "../../../../../../utils/currency";
import { capitalizeCurrencyCode } from "../../../../../../utils/helpers";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import CustomRadio from "../../../../../../components/common/RadioInput";
import { CNYProps, FormInputs } from "../../../../../../types/walletTypes";

type WalletProps = {
  currency: string;
};

const Amount = ({ handleNext }: CNYProps) => {
  const getSavedData = useAppSelector((state) => state.formData.cnyData);

  const {
    watch,
    control,
    handleSubmit,
    resetField,
    formState: { errors },
  } = useForm<FormInputs>({
    defaultValues: getSavedData || {},
  });

  const CNYData = useAppSelector((state) => state.formData?.cnyData || {});
  const [userData, setUserData] = useState<FormInputs>(CNYData);
  const { keys } = useAppSelector((state) => state.settings);
  const { wallets, loading } = useAppSelector((state) => state?.wallets || {});
  const { businessId } = useAppSelector((state) => state.users);
  const { currencyCode } = useParams();
  const dispatch = useAppDispatch();

  const filteredWallets = wallets as WalletProps[];

  const getAllowedPurposes = (paymentMethod: string) => {
    const config = paymentMethodConfigurations[paymentMethod];
    if (!config) return [];

    const key = `${config.transferType}_${config.service}`;
    const allowedNames = allowedPurposeMap[key];
    if (!allowedNames) return cnyPaymentPurpose;

    return cnyPaymentPurpose.filter((option) =>
      allowedNames.includes(option.value),
    );
  };

  const IDENTITY_TYPE = watch("senderIdentity");
  const RECEIVER_TYPE = watch("accountType");
  const SENDER_TYPE = watch("receiverAccountType");

  const senderTypeOptions = senderTypeMap[IDENTITY_TYPE];

  const receiverTypeOptions = receiverTypeMap[RECEIVER_TYPE];

  const CURRENT_WALLET = wallets.find((wallet) => wallet.currency === "USD");

  const uniqueCurrencies = Array.from(
    new Set(filteredWallets?.map((wallet) => wallet.currency)),
  );

  const resetUserData = (data: FormInputs): FormInputs => {
    return Object.keys(data).reduce((acc, key) => {
      acc[key as keyof FormInputs as any] = null;
      return acc;
    }, {} as FormInputs);
  };

  const options = uniqueCurrencies.map((currency) => ({
    label: currency,
    value: currency,
  }));

  useEffect(() => {
    if (CURRENT_WALLET.availableBalance < userData.amount) {
      handleDataUpdate("balanceError", true);
    } else {
      handleDataUpdate("balanceError", false);
    }
  }, [CURRENT_WALLET, userData.amount]);

  /*
                     Remove USD and allow other currency options when business decides otherwise
                         */
  const walletToDebit = options?.filter((option) => option.value === "USD");

  const handleAmountChange = (enteredAmount) => {
    setUserData((prevData) => ({
      ...prevData,
      amount: enteredAmount,
    }));
  };

  const handleDataUpdate = (field: keyof FormInputs, value: any) => {
    setUserData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  useEffect(() => {
    dispatch(savedCNYData(userData));
  }, [userData, dispatch]);

  useEffect(() => {
    if (businessId) {
      dispatch(merchantBusinessKeys(businessId));
    }
  }, [businessId, dispatch]);

  useEffect(() => {
    const fetchData = async () => {
      const action = await dispatch(getCNYQuotationCategory());
      if (getCNYQuotationCategory.fulfilled.match(action)) {
        handleDataUpdate("category", action.payload);
      }
    };

    fetchData();
  }, [dispatch]);

  const debouncedGetKlashaQuotation = useCallback(
    debounce(async () => {
      if (
        !userData?.amount ||
        !userData?.paymentMethod ||
        !userData?.purpose ||
        !keys?.data?.encryptionKey
      ) {
        return;
      }

      const config = paymentMethodConfigurations[userData.paymentMethod] || {
        serviceCode: "",
        service: "",
        fundSource: "",
      };

      const data = {
        sourceCurrency: "USD",
        destinationCurrency: "CNY",
        destinationAmount: userData.amount,
        ...config,
      };

      const encryptedData = encryptAlgorithm(data, keys?.data?.encryptionKey);
      const payload = { message: encryptedData };
      const token = keys?.data?.publicKey;

      const action = await dispatch(
        KlashaCNYQuotation({ message: payload, token }),
      );

      if (KlashaCNYQuotation.fulfilled.match(action)) {
        const { fxRate, fee, sourceAmount, expiration, id, reference } =
          action.payload;

        const convertedValue = Number(userData?.amount / fxRate);

        setUserData((prev) => ({
          ...prev,
          rate: fxRate,
          convertedAmount: convertedValue,
          convertedRate: convertedValue,
          totalAmount: Number(sourceAmount + fee),
          fee,
          expiration,
          quotationId: id,
          reference,
          sourceAmount,
        }));
      }
    }, 1000),
    [dispatch, userData?.amount, userData?.paymentMethod, userData?.purpose],
  );

  const { amount, paymentMethod, purpose } = userData;

  useEffect(() => {
    if (amount > 0 && paymentMethod && purpose && keys?.data?.encryptionKey) {
      debouncedGetKlashaQuotation();
    }
  }, [amount, paymentMethod, purpose, keys]);

  useEffect(() => {
    if (!amount) {
      setUserData((prev) => resetUserData(prev));
    }
  }, [amount]);

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    const updatedData: FormInputs = {
      ...data,
      senderIdentity: data.senderIdentity,
      senderIdType: data.senderIdType,
      receiverIdType: data.receiverIdType,
      quotationId: userData?.quotationId,
      paymentMethod: userData.paymentMethod,
      purpose: userData.purpose,
    };

    dispatch(savedCNYData(updatedData));
    handleNext?.();
  };

  return (
    <ContentWrapper>
      <Loader isLoading={loading} />

      <InputWrapper>
        <GenericTitle>
          Are you the ultimate sender for this transaction?
        </GenericTitle>
        <GenericSubTitle>
          Accurate identification is crucial for compliance with financial
          regulations.
        </GenericSubTitle>
        <Controller
          name="senderIdentity"
          control={control}
          rules={{ required: "This field is required" }}
          render={({ field }) => (
            <ItemWrapper>
              {["Yes", "No"].map((option) => (
                <RWrapper key={option}>
                  <CustomRadio
                    label={option}
                    value={option}
                    onClick={() => {
                      field.onChange(option);
                      resetField("receiverAccountType");
                      resetField("accountType");

                      handleDataUpdate("paymentMethod", null)
                    }}
                    selected={field.value === option}
                  />
                </RWrapper>
              ))}
            </ItemWrapper>
          )}
        />
        {<Error>{errors?.senderIdentity?.message}</Error>}
      </InputWrapper>

      {IDENTITY_TYPE && (
        <InputWrapper>
          <Controller
            key={IDENTITY_TYPE}
            name="accountType"
            control={control}
            rules={{ required: "Sender type is required" }}
            render={({ field }) => (
              <CustomSelect
                value={field.value}
                options={senderTypeOptions}
                onChange={(val) => {
                  field.onChange(val);
                  resetField("receiverAccountType");
                  handleDataUpdate("paymentMethod", null)
                }}
                $width="100%"
                placeholder="Sender type"
                lightBorder
              />
            )}
          />
          {<Error>{errors?.senderIdType?.message}</Error>}
        </InputWrapper>
      )}

      <InputWrapper>
        <Controller
          name="receiverAccountType"
          control={control}
          rules={{ required: "Receiver type is required" }}
          render={({ field }) => (
            <CustomSelect
              value={field.value}
              options={receiverTypeOptions}
              onChange={field.onChange}
              $width="100%"
              placeholder="Receiver type"
              lightBorder
            />
          )}
        />
        {<Error>{errors?.receiverIdType?.message}</Error>}
      </InputWrapper>

      <CustomSelect
        $width="100%"
        placeholder="Payment method"
        options={getFilteredPaymentMethods(SENDER_TYPE, RECEIVER_TYPE).map(
          (option) => ({
            label: option.name,
            value: option.value,
          }),
        )}
        onChange={(data) => handleDataUpdate("paymentMethod", data)}
        value={userData?.paymentMethod}
        lightBorder
      />

      <CustomSelect
        $width="100%"
        placeholder="Purpose"
        options={getAllowedPurposes(paymentMethod).map((option) => ({
          label: option.name,
          value: option.value,
        }))}
        onChange={(data) => handleDataUpdate("purpose", data)}
        value={userData?.purpose}
        lightBorder
      />

      <InputAndSelect
        amountValue={userData?.amount}
        width="100%"
        placeholder="Amount"
        options={[{ label: "CNY", value: "CNY" }]}
        selectValue={capitalizeCurrencyCode(currencyCode)}
        onAmountChange={handleAmountChange}
      />

      {userData.rate && (
        <section>
          <RateBox>
            <span className="fee-box">
              x
              <span>
                {formatCurrencyAmount("", userData?.rate ?? 0, 2) ?? 0}
              </span>
            </span>
            <span>Exchange rate</span>
          </RateBox>
        </section>
      )}

      {userData.fee && (
        <section>
          <RateBox>
            <span className="fee-box">
              +
              <span>
                {formatCurrencyAmount("", userData?.fee ?? 0, 2) ?? 0}
              </span>
            </span>
            <span>Fees</span>
          </RateBox>
        </section>
      )}

      {userData?.totalAmount > 0 && (
        <div>
          <InputAndSelect
            amountValue={userData?.totalAmount?.toFixed(2) ?? ""}
            width="100%"
            placeholder="Total you will be charged"
            options={walletToDebit}
            selectValue={"USD"}
          />
          {userData?.balanceError && (
            <BalanceExceptionWrapper>
              <NotifyIcon /> <span>Exceeds your wallet balance.</span>
              <Link to="/wallets/usd"> Top-up funds</Link>
            </BalanceExceptionWrapper>
          )}
        </div>
      )}

      <Button
        label="Continue"
        onClick={handleSubmit(onSubmit)}
        height="48px"
        disabled={
          loading ||
          !userData?.rate ||
          !userData?.paymentMethod ||
          !userData?.purpose ||
          userData?.balanceError
        }
      />
    </ContentWrapper>
  );
};

export default Amount;
